export const WIDTH_BREAKPOINTS = {
  xs: "400px",
  sm: "600px",
  md: "900px",
  lg: "1200px",
  xl: "1400px",
};

export const APPRV = {
  APPROVAL: "승인",
  REVIEW: "검토",
};

export const APPRV_CD = {
  APPROVAL: "APTPA",
  PAYMENT: "APTPE",
  REVIEW: "APTPC",
  STEP: {
    APPROVAL: "APSPA",
    REJECT: "APSPD",
  },
};

export const THEME = {
  RED: "red",
  ORANGE: "orange",
  BLUE: "blue",
  GREEN: "green",
  DEFAULT: "light blue",
};

export const EXIST = "ifAuth";
export const CREATE_TAB_LIST = ["SYS110"]; // Program ID
export const PROJECT_NAME_MAX_LENGTH = 100;

/* ------------------------------------------------------------------ engine */
/* FE-04 — the runtime engine backing a MAXIS project. `ENGINE_TYPE` is the
   neutral code the product services carry; it is never a provider id, url or
   key. Kept here (not in enumerates.js) because the screens read it as a plain
   display constant. */

export const ENGINE_TYPE = {
  DIFY: "dify",
  LANGFLOW: "langflow",
};

/** Engine shown when a row carries no ENGINE_TYPE — every as-is row is Dify. */
export const DEFAULT_ENGINE_TYPE = ENGINE_TYPE.DIFY;

/** Badge text per engine. */
export const ENGINE_LABEL = {
  [ENGINE_TYPE.DIFY]: "Dify",
  [ENGINE_TYPE.LANGFLOW]: "Langflow",
};

/** Badge tone per engine — maps onto the THEME palette above. */
export const ENGINE_BADGE_THEME = {
  [ENGINE_TYPE.DIFY]: THEME.BLUE,
  [ENGINE_TYPE.LANGFLOW]: THEME.GREEN,
};

/**
 * The engine catalog — what a code may render as, not what a user may pick.
 *
 * There is no per-project engine choice. RS-01 shipped an **additive** model:
 * `/project/approval` binds every engine in the deployment's
 * `INTEGRATION_ENGINES` list, so a project runs on all of them and the creator
 * decides nothing. `/project/create` has no `ENGINE_TYPE` field at all — a value
 * sent there is dropped by the server.
 *
 * `AVAILABLE` therefore no longer gates a picker; it is kept because
 * `creatableEngineOptions()` and its tests still read it, and because adding a
 * third engine to this list must not silently start offering it anywhere.
 */
export const ENGINE_OPTIONS = [
  { ENGINE_CD: ENGINE_TYPE.DIFY, ENGINE_NM: "Dify", AVAILABLE: true },
  { ENGINE_CD: ENGINE_TYPE.LANGFLOW, ENGINE_NM: "Langflow", AVAILABLE: true },
];

/** Description shown under the engine choice on the project-creation form. */
export const ENGINE_HELP_TEXT = {
  [ENGINE_TYPE.DIFY]: "기존 방식입니다. 멤버 초대와 확장 설치를 지원합니다.",
  [ENGINE_TYPE.LANGFLOW]: "프로젝트당 전용 사용자로 동작합니다.",
};

/** The engine cannot be changed once the project is bound to it. */
export const ENGINE_LOCKED_TEXT = "실행 엔진은 프로젝트 생성 시에만 선택할 수 있습니다.";

/**
 * FE-02 — where the engine is actually bound.
 *
 * FE_INTEGRATION_API.md §1: "Create **chỉ ghi DB**, chưa đụng engine nào" — the
 * engine fan-out (Gitea, LiteLLM, Vault, Dify tenant, then Langflow bind +
 * provision + sync model) happens in POST /project/approval, not
 * POST /project/create. So a project is only really on an engine after approval.
 */
export const ENGINE_BOUND_AT_APPROVAL_TEXT =
  "실행 엔진은 승인 완료 후 실제로 연결됩니다.";

/**
 * §1: "`success: true` KHÔNG có nghĩa phần Langflow đã xong" — the engine
 * fan-out is wrapped in try/except so an engine failure still returns success.
 * The only way to know is POST /project/engine-url; 404 means not bound.
 */
export const ENGINE_APPROVAL_UNCONFIRMED_TEXT =
  "승인은 완료되었으나 엔진 연결 여부는 별도로 확인해야 합니다.";

/**
 * §1: approval crashes when the project has no member
 * ("`USER_LIST` phải có ≥1 user tồn tại trong `E3_USER`. Rỗng thì approval sẽ
 * crash ở project.py:288 — FE nên chặn ở form"). The form has to block first.
 */
export const PROJECT_NEEDS_MEMBER_TEXT = "프로젝트 책임자는 필수사항입니다.";

/**
 * §1, POST /project/update: renaming reaches MAXIS DB, the Gitea org and the
 * Dify workspace. "Langflow không có gì để đổi tên" — a Langflow project's
 * "workspace" is the project's dedicated user, and no object carries the project
 * name. The screen must not promise otherwise.
 */
export const ENGINE_RENAME_NOT_APPLIED_TEXT =
  "Langflow에서는 프로젝트 이름이 변경되지 않습니다.";

/**
 * §3: the launch cookie only travels when the engine console sits on the MAXIS
 * origin. `engine_connection.console_base_url` holds the engine's own address on
 * a stock deployment, so the console opens in a new tab and the user signs in
 * there. Say that rather than let a login screen look like a failure.
 */
export const ENGINE_CROSS_ORIGIN_LOGIN_TEXT =
  "엔진 콘솔이 MAXIS와 다른 주소에서 열립니다. 로그인 화면이 표시되면 직접 로그인해 주세요.";

/**
 * Fallback when POST /project/engine-url refuses the launch and sends no message.
 *
 * Deliberately not "this project does not use this engine": 404 also comes back
 * when the engine DB could not be read (`has_active_binding` swallows the error
 * so an engine incident cannot break the Dify flow), so the screen must not
 * state the binding is absent.
 */
export const ENGINE_LAUNCH_UNAVAILABLE_TEXT =
  "해당 엔진에 연결되어 있지 않거나 연결 상태를 확인할 수 없습니다.";

/**
 * FE-04 — display text for the neutral refusal reason codes the Integration
 * Service returns (design rev 5 error catalog). The code itself stays the
 * contract; this map is presentation only.
 */
export const REFUSAL_REASON_TEXT = {
  RUNTIME_UNSUPPORTED: "현재 엔진에서 지원하지 않는 기능입니다.",
  RUNTIME_DEFER_LIVE: "아직 활성화되지 않은 기능입니다.",
  RUNTIME_NOT_BACKED: "엔진이 아닌 MAXIS에서 처리하는 기능입니다.",
  RUNTIME_BLOCKED: "대상 버전 제약으로 차단된 기능입니다.",
  RUNTIME_DISABLED: "해당 엔진이 비활성화되어 있습니다.",
  RUNTIME_UNDECLARED: "지원 여부가 선언되지 않은 기능입니다.",
};

/** Shown when an operation is substituted but the engine sent no label. */
export const SUBSTITUTE_FALLBACK_LABEL = "대체 경로로 실행됨";

/** Shown when the capability inventory could not be read. */
export const CAPABILITY_DEGRADED_TEXT = "엔진 지원 정보를 불러오지 못했습니다.";

/* ------------------------------------------------- Langflow chat (SRV420) */
const LANGFLOW_UNREACHABLE_TEXT = "Langflow 채팅 서비스에 연결하지 못했습니다. 잠시 후 다시 시도해 주세요.";
const LANGFLOW_STREAM_CUT_TEXT = "답변이 끝나기 전에 연결이 끊어졌습니다. 다시 시도해 주세요.";

/**
 * What the chat screen says when a Langflow chat turn fails, by the code of the
 * error event. The codes come from model-management (its own and the
 * Integration Service's, passed on as they are) and from the backend, which
 * adds CHAT_SERVICE_UNAVAILABLE and CHAT_STREAM_CUT; the screen uses the same
 * two when the backend itself cannot be reached or the connection to it is cut.
 * The set is open: a code missing here gets LANGFLOW_CHAT_ERROR_FALLBACK, never
 * silence. One code has one text wherever it is shown. The message text of the
 * event is written for operators, in English, and is not shown.
 */
export const LANGFLOW_CHAT_ERROR_TEXT = {
  CHAT_SERVICE_UNAVAILABLE: LANGFLOW_UNREACHABLE_TEXT,
  INTEGRATION_UNREACHABLE: LANGFLOW_UNREACHABLE_TEXT,
  PROVIDER_UNAVAILABLE: LANGFLOW_UNREACHABLE_TEXT,
  LANGFLOW_UNAVAILABLE: "이 서버에는 Langflow가 설정되어 있지 않습니다.",
  CHAT_STREAM_CUT: LANGFLOW_STREAM_CUT_TEXT,
  INTEGRATION_STREAM_CUT: LANGFLOW_STREAM_CUT_TEXT,
  LANGFLOW_IDLE_TIMEOUT: "Langflow가 응답하지 않습니다. 잠시 후 다시 시도해 주세요.",
  NO_ANSWER: "플로우가 답변 없이 종료되었습니다. Langflow에서 플로우를 확인해 주세요.",
  PROVIDER_CALL_FAILED: "플로우 실행 중 오류가 발생했습니다. Langflow에서 플로우를 확인해 주세요.",
  CONTRACT_VIOLATION: "이 플로우는 채팅으로 실행할 수 없습니다.",
  RUNTIME_UNDECLARED: REFUSAL_REASON_TEXT.RUNTIME_UNDECLARED,
};

/** A Langflow chat error whose code is unknown or unreadable. */
export const LANGFLOW_CHAT_ERROR_FALLBACK = "Langflow 답변 중 오류가 발생했습니다.";

/** A refused Langflow chat turn whose answer carries no readable text. */
export const LANGFLOW_CHAT_REFUSED_FALLBACK = "요청을 처리하지 못했습니다.";

/** Under a question of a reopened Langflow chat that the engine marked failed. */
export const LANGFLOW_HISTORY_FAILED_TEXT = "이 질문은 오류로 답변을 받지 못했습니다.";

/* --------------------------------------------- Play Ground chat (SRV420) */
/** In the chat window while the history of the chat just opened is loading. */
export const CHAT_HISTORY_LOADING_TEXT = "채팅 내역을 불러오는 중입니다...";

/** In an opened chat whose history could not be loaded: the chat is open all the same. */
export const CHAT_HISTORY_FAILED_TEXT = "채팅 내역을 불러오지 못했습니다. 이 채팅에 이어서 질문할 수 있습니다.";

/**
 * How long sending waits for the history of the chat just opened. The backend
 * bounds the call it makes for it (a 30s response timeout, then 5s more for
 * its own thread): past that no answer will come, the rest is the network.
 */
export const CHAT_HISTORY_WAIT_MS = 40_000;

/**
 * How many chat turns the page runs at once, on screen and in the background.
 * Each holds a connection to the server for as long as it answers, and over
 * HTTP/1.1 a browser opens six at most to one server, shared by every request
 * of the page: six turns would leave it none for the chat list, the history or
 * any other screen. Three leave half. The next question waits; no turn is
 * stopped to make room.
 */
export const CHAT_TURNS_MAX = 3;

/**
 * Rows of one page of the play list, the chat list and a chat's history (turns).
 * Dify's own default page; the services take 1-100.
 */
export const PLAYGROUND_PAGE_SIZE = 20;

/** At the end of a play-list tab or of the chat list while its next page loads. */
export const LIST_LOADING_MORE_TEXT = "불러오는 중...";

/** At the end of a play-list tab or of the chat list when its next page could not be loaded; clicking it asks again. */
export const LIST_MORE_FAILED_TEXT = "목록을 더 불러오지 못했습니다. 다시 시도";

/** In the Langflow tab of the play list when its flows could not be loaded. */
export const LANGFLOW_PLAY_LIST_FAILED_TEXT = "Langflow 목록을 불러오지 못했습니다.";

/** At the end of the Langflow tab when a page passed over a project it could not read now; clicking it loads the tab again. */
export const LANGFLOW_PROJECT_LEFT_OUT_TEXT = "일부 프로젝트의 플로우를 불러오지 못했습니다. 다시 불러오기";

/** In the Langflow tab of the play list when no project of the user has a Langflow chat flow. */
export const LANGFLOW_PLAY_LIST_EMPTY_TEXT = "Langflow 채팅 플로우가 없습니다.";

/** At the top of an opened chat whose older turns are not shown yet. */
export const CHAT_OLDER_TEXT = "이전 대화 불러오기";

/** At the top of an opened chat while its older turns load. */
export const CHAT_OLDER_LOADING_TEXT = "이전 대화를 불러오는 중...";

/** At the top of an opened chat when its older turns could not be loaded; clicking it asks again. */
export const CHAT_OLDER_FAILED_TEXT = "이전 대화를 불러오지 못했습니다. 다시 시도";

/** Under the question box while CHAT_TURNS_MAX turns are running. */
export const CHAT_TURNS_FULL_TEXT = `답변을 기다리는 질문이 ${CHAT_TURNS_MAX}개입니다. 답변이 하나 끝나면 보낼 수 있습니다.`;

export const PropConst = {
  authBtn: {
    ADD: "ADD",
    APPLY: "APPLY",
    APPR: "APPR",
    ATTACH: "ATTACH",
    BARCODEPRINT: "BARCODEPRINT",
    CANCEL: "CANCEL",
    CHANGE: "CHANGE",
    CLOSE: "CLOSE",
    COMPLETE: "COMPLETE",
    CONFIRM: "CONFIRM",
    COPY: "COPY",
    DELETE: "DELETE",
    DOWNLOAD: "DOWNLOAD",
    EXCEL: "EXCEL",
    HMODIFY: "HMODIFY",
    INIT: "INIT",
    MODIFY: "MODIFY",
    NONE: "NONE",
    OK: "OK",
    PRINT: "PRINT",
    RECOMMEND: "RECOMMEND",
    RECORDREV: "RECORDREV",
    REGISTE: "REGISTE",
    REJECT: "REJECT",
    RELOAD: "RELOAD",
    REMOVE: "REMOVE",
    RENTAL: "RENTAL",
    REQUEST: "REQUEST",
    RESET: "RESET",
    RETURN: "RETURN",
    REVISION: "REVISION",
    SAVE: "SAVE",
    SAVEALL: "SAVEALL",
    SEARCH: "SEARCH",
    SEARCHBOX: "SEARCHBOX",
    SETTING: "SETTING",
    UNSEARCHBOX: "UNSEARCHBOX",
    UPLOAD: "UPLOAD",
    WARINGSIGN: "WARINGSIGN",
  },
  buttonType: {
    default: "default",
    close: "close",
    search: "search",
  },
  iconType: {
    btn_add:"btn_add",
    btn_min: "btn_min",
    btn_copy: "btn_copy",
    btn_delete: "btn_delete",
    btn_trash: "btn_trash",
    btn_cancel: "btn_cancel",
    btn_close: "btn_close",
    btn_return: "btn_return",
    btn_ok: "btn_ok",
    btn_save: "btn_save",
    btn_floppy: "btn_floppy",
    btn_new: "btn_new",
    btn_file_up: "btn_file_up",
    btn_file_down: "btn_file_down",
    btn_cloud_up: "btn_cloud_up",
    btn_cloud_down: "btn_cloud_down",
    btn_excel: "btn_excel",
    btn_excel: "btn_clip",
    btn_link: "btn_link",
    btn_img: "btn_img",
    btn_popup: "btn_popup",
    btn_search:"btn_search",
    btn_print:"btn_print",
    btn_day:"btn_day",
    btn_list:"btn_list",
    btn_pen:"btn_pen",
    btn_order:"btn_order",
    btn_apply:"btn_apply",
    btn_warning:"btn_warning",
    btn_up:"btn_up",
    btn_down: "btn_down",
    btn_left: "btn_left",
    btn_right: "btn_right",
    btn_reset: "btn_reset",
    btn_add_w: "btn_add_w",
    btn_confirm_w: "btn_confirm_w",
  },
};
